import { OptionDefinition } from "@amzn/awsui-components-core/internal/components/option/interfaces";
import {
  DEFAULT_OPTION,
  DEFAULT_STAGE,
  PAYMENT_METHOD_CLASS_OPTIONS,
  PAYMENT_METHOD_OPTIONS,
  PAYMENT_TYPE_OPTIONS,
  STAGES,
  TIME_RANGES,
} from "../config/recentTransactionTableConfig";
import {
  PaymentType,
  RecentTransactionsSearchParams,
  SearchFilters,
} from "../types/common";
import { Stage } from "../lusca-search-api/generated-src";
import { GL_MAPPING } from "../hooks/useSummaryData";

export const DEFAULT_FILTERS: SearchFilters = {
  stage: DEFAULT_STAGE,
  gl: DEFAULT_OPTION,
  marketplaces: [],
  usecases: [],
  paymentMethod: PAYMENT_METHOD_OPTIONS[0],
  paymentType: PAYMENT_TYPE_OPTIONS[0],
  paymentMethodClass: PAYMENT_METHOD_CLASS_OPTIONS[0],
  asin: "",
  timeRange: TIME_RANGES[1],
  date: "",
  deep: false,
  features: [],
};

export function getEndTime(
  range: OptionDefinition,
  date: string
): number | undefined {
  if (range.value === "custom" && date) {
    const d = new Date(date);
    d.setUTCHours(12, 0, 0, 0);
    return Math.floor(d.getTime() / 1000);
  }
  const now = Math.floor(Date.now() / 1000);
  switch (range.value) {
    case "5min":
      return now - 5 * 60;
    case "15min":
      return now - 15 * 60;
    case "1hour":
      return now - 60 * 60;
    case "4hours":
      return now - 60 * 60 * 4;
    case "24hours":
      return now - 24 * 60 * 60;
    case "1week":
      return now - 7 * 24 * 60 * 60;
    case "1month":
      return now - 30 * 24 * 60 * 60;
    default:
      return undefined;
  }
}

export function parseFiltersFromUrl(params: URLSearchParams): SearchFilters {
  const getValue = (key: string) => params.get(key);

  const toOptionList = (key: string): OptionDefinition[] =>
    params
      .getAll(key)
      .flatMap((v) => v.split(",")) // backwards compatible for when urls used commas
      .filter(Boolean)
      .map((v) => ({ value: v, label: v }));

  const stage =
    STAGES.find((o) => o.value === getValue("stage")) || DEFAULT_STAGE;

  const glValue = getValue("gl");
  const gl = glValue
    ? { value: glValue, label: GL_MAPPING[glValue] ?? glValue }
    : DEFAULT_OPTION;

  const paymentMethod =
    PAYMENT_METHOD_OPTIONS.find((o) => o.value === getValue("paymentMethod")) ||
    DEFAULT_OPTION;

  const paymentType =
    PAYMENT_TYPE_OPTIONS.find((o) => o.value === getValue("paymentType")) ||
    PAYMENT_TYPE_OPTIONS[0];

  const paymentMethodClass =
    PAYMENT_METHOD_CLASS_OPTIONS.find(
      (o) => o.value === getValue("paymentMethodClass")
    ) || PAYMENT_METHOD_CLASS_OPTIONS[0];

  const timeRange =
    TIME_RANGES.find((o) => o.value === getValue("timeRange")) ||
    TIME_RANGES[1];

  const date = getValue("date") ?? "";
  const asin = getValue("asin") ?? "";

  const marketplaces = toOptionList("countries");
  const usecases = toOptionList("usecases");
  const features = toOptionList("features");

  const deep = getValue("deep") === "true";

  return {
    stage,
    gl,
    marketplaces,
    usecases,
    paymentMethod,
    paymentType,
    paymentMethodClass,
    asin,
    timeRange,
    date,
    deep,
    features,
  };
}

export function parseEndTimeFromUrl(
  params: URLSearchParams
): number | undefined {
  const endTime = params.get("endTime");
  if (!endTime) {
    return undefined;
  }

  const parsed = Number(endTime);
  return Number.isFinite(parsed) ? parsed : undefined;
}

function nonDefault(opt: OptionDefinition): string | undefined {
  return opt.value !== DEFAULT_OPTION.value ? opt.value : undefined;
}

function listValues(list: OptionDefinition[]): string[] | undefined {
  const values = list.map(nonDefault).filter((v): v is string => !!v);

  return values.length ? values : undefined;
}

export function buildApiParams(
  filters: SearchFilters
): RecentTransactionsSearchParams {
  const params: RecentTransactionsSearchParams = {
    stage: filters.stage.value! as Stage,
  };

  const gl = nonDefault(filters.gl);
  if (gl) params.gl = gl;

  const countries = listValues(filters.marketplaces);
  if (countries) params.countries = countries;

  const usecases = listValues(filters.usecases);
  if (usecases) params.usecases = usecases;

  const paymentMethod = nonDefault(filters.paymentMethod);
  if (paymentMethod) params.paymentMethod = paymentMethod;

  const paymentType = nonDefault(filters.paymentType);
  if (paymentType) params.paymentType = paymentType as PaymentType;

  const paymentMethodClass = nonDefault(filters.paymentMethodClass);
  if (paymentMethodClass) params.paymentMethodClass = paymentMethodClass;

  if (filters.asin) params.asin = filters.asin;

  const features = listValues(filters.features);
  if (features) params.features = features;

  params.endTime = getEndTime(filters.timeRange, filters.date);

  if (filters.deep) {
    params.deep = true;
  }
  return params;
}

export function serializeSearchParams(
  params: RecentTransactionsSearchParams
): URLSearchParams {
  const sp = new URLSearchParams();
  Object.entries(params).forEach(([key, val]) => {
    if (val == null) {
      return;
    }

    if (Array.isArray(val)) {
      val.forEach((v) => sp.append(key, String(v)));
    } else {
      sp.set(key, String(val));
    }
  });

  return sp;
}

export function serializeFilterState(filters: SearchFilters): URLSearchParams {
  const apiParams = buildApiParams(filters);
  const sp = serializeSearchParams(apiParams);

  if (
    filters.timeRange.value !== "custom" ||
    apiParams.endTime == null ||
    !Number.isFinite(Number(apiParams.endTime))
  ) {
    sp.delete("endTime");
  }

  if (filters.timeRange.value) {
    sp.set("timeRange", String(filters.timeRange.value));
  }

  if (filters.date) {
    sp.set("date", filters.date);
  }

  return sp;
}
